import { useStoryDataContext } from "contexts/StoryContext";
import { AnimatePresence, motion } from "framer-motion";
import { ICandle } from "../../organisms/StoryFace/storyFace.types";
import { IStory } from "../../../pages/People/people.types";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Computes the expiration time (in ms) of the last chain of candles.
 * Candles are sorted chronologically and chained together:
 * if a candle is created before the current chain expires, its lifetime extends the chain.
 * Otherwise, a new chain is started.
 */
function computeChainExpiration(candles: any[]): number {
  if (!candles || candles.length === 0) return 9;

  // Sort candles by created_at ascending.
  const sorted = candles.slice().sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  let currentChainStart = new Date(sorted[0].created_at).getTime();
  let currentChainExpiration =
    currentChainStart + sorted[0].candle_lifetime_days * MS_PER_DAY;

  for (let i = 1; i < sorted.length; i++) {
    const candle = sorted[i];
    const candleTime = new Date(candle.created_at).getTime();
    const candleLifetimeMs = candle.candle_lifetime_days * MS_PER_DAY;

    if (candleTime <= currentChainExpiration) {
      // Candle falls within the current chain – extend the chain.
      currentChainExpiration += candleLifetimeMs;
    } else {
      // Candle is created after the current chain expired; start a new chain.
      currentChainStart = candleTime;
      currentChainExpiration = candleTime + candleLifetimeMs;
    }
  }
  return currentChainExpiration;
}

/**
 * Calculates the darkness level based on how many days have passed since the last chain ended.
 * - 0 means 0 days passed.
 * - 9 means 180 or more days have passed.
 * Values in between are mapped linearly.
 */
const calculateDarknessLevel = (candles: ICandle[]): number => {
  if (!candles || candles.length === 0) return 0;

  const chainExpiration = computeChainExpiration(candles);
  const now = Date.now();

  let tillEnd = (chainExpiration - now) / MS_PER_DAY;

  if (tillEnd > 0) {
    tillEnd = 0;
  }
  const maxDays = 180;

  const level = Math.max(0, Math.floor(-tillEnd / maxDays * 9));

  return level;
};

const computeGradient = (level: number) =>
  `radial-gradient(ellipse 90% 40% at center top, rgba(255,255,255,0) 0%, rgba(63,43,30,0) 30%, rgba(63,43,30,0.${level}) 80%, rgba(63,43,30,0.${level}) 100%)`;


const calculateFromStoryCreateDate = (story?: IStory | null): number => {

  if (!story || !story.created_at) return 0;
  const createdMs = Date.parse(story.created_at);
  const now = Date.now();

  const daysPassed = (now - createdMs) / MS_PER_DAY;
  const bucketSize = 180 / 9;

  const rawIndex = Math.floor(daysPassed / bucketSize);
  const index    = Math.min(Math.max(rawIndex, 0), 9);

  return index;
};

function GradientOverlay() {
  const {candles, story} = useStoryDataContext();

  const darknessLevel = !!candles?.results.length
    ? calculateDarknessLevel(candles.results)
    : calculateFromStoryCreateDate(story);

  return (
    <AnimatePresence>
      {!story?.is_alive && (
        <motion.div
          initial={{background: computeGradient(0)}}
          animate={{background: computeGradient(darknessLevel)}}
          transition={{duration: 0.5}}
          exit={{background: computeGradient(0)}}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 991,
          }}
        />
      )}
    </AnimatePresence>
  );
}

export default GradientOverlay;
